import { useEffect, useState } from "react";
import { AllAudits } from "./admin/AllAudits";
import { AuditRecord } from "./admin/AuditRecord";
import { Costing } from "./admin/Costing";
import { PlatformConfiguration } from "./admin/PlatformConfiguration";
import { TenantRegister } from "./admin/TenantRegister";
import { MyAudits } from "./auditor/MyAudits";
import { MyCompetenceRecord } from "./auditor/MyCompetenceRecord";
import { AuditWorkspace } from "./audit/AuditWorkspace";
import { AUDIT_STEPS, type StepKey } from "./audit/steps";
import { AuditeePortal } from "./portal/AuditeePortal";
import { ForcedPasswordChange, SetPassword } from "./SetPassword";
import { VerificationQueue } from "./admin/VerificationQueue";
import { VerifyCertificationBody } from "./admin/VerifyCertificationBody";
import {
  ApiError,
  authenticate,
  currentSignedIn,
  onSignedOut,
  readApplication,
  requestPasswordReset,
  signOut,
  signUp,
  type SignedIn,
} from "./api/client";
import { AuditsList } from "./cb/AuditsList";
import { IntakeForm } from "./cb/IntakeForm";
import { OrganisationProfile } from "./cb/OrganisationProfile";
import { useBrandMark } from "./cb/useBrandMark";
import { AppShell } from "./components/AppShell";
import { TextField } from "./components/TextField";
import { CertificationBodyOnboarding } from "./onboarding/CertificationBodyOnboarding";
import { AuditorRegister } from "./register/AuditorRegister";
import { ConfirmRegistration } from "./ConfirmRegistration";
import { navigate, segments, useFragment, usePath } from "./routing";
import { IssuedReport } from "./portal/IssuedReport";
import { VerifyCompetenceRecord } from "./register/VerifyCompetenceRecord";

/**
 * Sign in or register, then the screen the role has earned.
 *
 * A certification body registers itself with an address and waits: the account exists but
 * cannot sign in until a Complao administrator activates it (ADR 0013). Registration is
 * the only unauthenticated call on the platform, and it hands back nothing usable, so the
 * open form is not itself a way in.
 *
 * The door admits three roles. An administrator lands on the certification body register; a
 * certification body lands on its own application; a lead auditor lands on their own
 * competence record. The auditor was refused here until M1-5, and the reason was that there
 * was nothing to land on — screens 12 to 18 are that something, and nobody else can fill
 * them in on their behalf. What admits an auditor is the *invitation*, not the role, and
 * that refusal is still the server's: the browser only renders what came back.
 */
export function App() {
  // Restored from the tab's storage rather than started at null: a page refresh is not a
  // sign-out, and the token behind this is one the server would still honour.
  const [signedIn, setSignedIn] = useState<SignedIn | null>(currentSignedIn);
  // The other half of that: the server can end a session this browser is still holding, and
  // the client drops the stored copy the moment a call comes back 401. This follows it on the
  // screen, so the caller lands on the door instead of on a shell with nothing behind it.
  // Declared above every early return below — the returns are conditional and a hook may not be.
  useEffect(() => {
    onSignedOut(() => setSignedIn(null));
    return () => onSignedOut(null);
  }, []);

  // Read from the location on every move rather than once at mount — `useFragment`. A
  // second link pasted over a first is a same-document navigation, so a token held in state
  // would be the first one for as long as the tab stayed open.
  const path = usePath();
  const fragment = useFragment();

  /**
   * The capability a mailed link carries, for the path that carries one.
   *
   * The fragment rather than the query string, because a fragment is never sent to the
   * server in a request line and never lands in an access log or a `Referer` header. All
   * four links the platform mails are credentials and all four are handled like one.
   */
  const linkAt = (prefix: string): string | null => {
    if (!path.startsWith(prefix)) return null;
    return fragment.replace(/^#/, "").trim() || null;
  };
  const credentialLink = linkAt("/set-password");
  const confirmLink = linkAt("/confirm");

  /**
   * Leaving a mailed link, whichever of the two it was.
   *
   * The fragment is cleared first. A spent link left in the address bar is a credential in a
   * browser history and in every screenshot of it — and it is what the omnibox offers back
   * the next time the path is typed, which is how a link that was used once comes to be
   * presented again and met with *this link has already been used*.
   *
   * **And the tab's session goes with it.** The person now holding this tab arrived on a
   * link addressed to somebody else's account; returning them to a shell signed in as
   * whoever was here before hands them that account's screens. Signing out costs a
   * re-authentication and is the only ending that cannot do that.
   */
  const leaveLink = () => {
    // `navigate` rather than `replaceState` directly: it announces the move, which is what
    // the two hooks above read. A silent `replaceState` would leave the link on screen
    // with the address bar saying otherwise, which is the defect this pair was fixing.
    navigate("/", true);
    if (signedIn) {
      signOut();
      setSignedIn(null);
    }
  };

  // The auditee's link, before anything else. They hold a capability rather than an
  // account, so the doorway is not for them and there is nothing to sign them in to — the
  // link reaches one audit, and the shell around it is deliberately not the app's.
  const accessLink = linkAt("/portal");
  if (accessLink) return <AuditeePortal token={accessLink} />;

  // The report delivery link — M9-0, open item 71. A third capability-bearing path, on the
  // same terms: one report, no account, no shell.
  const reportLink = linkAt("/report");
  if (reportLink) return <IssuedReport token={reportLink} />;

  // The registration confirmation link — M9-0 phase 6b. Read before the doorway for the
  // reason the credential link is: the holder cannot get through it yet, and confirming is
  // what puts their registration in front of an administrator.
  if (confirmLink) {
    // Keyed by the credential. A second link arriving in the same document is a different
    // screen, and without the key the panel keeps the first one's outcome — a refusal about
    // a spent token drawn over a fresh one that works.
    return <ConfirmRegistration key={confirmLink} token={confirmLink} onDone={leaveLink} />;
  }

  // A credential link, on the same terms and for the same reason — M9-0. Both are read
  // before the doorway because their whole point is that the holder cannot get through it
  // yet: one has no account at all, the other has no usable password.
  //
  // **A session in this tab does not swallow either of them.** Both were guarded by
  // `!signedIn` and both stopped working in a tab that already held a sign-in — a link
  // opened in the tab an administrator was working in, or one whose sessionStorage Chrome
  // copied from the tab it was opened from, drew that administrator's shell and left the
  // person holding the link with no screen and no reason given. The four mailed
  // capabilities now behave alike: the portal and report links have always won over the
  // doorway, and these two join them. Nothing is granted by that — the link carries its own
  // credential, `send` puts the caller's own Authorization ahead of the session's, and the
  // three routes behind these screens read no session at all.
  //
  // Still no router, and this does not add one. Two path checks and a fragment is less
  // machinery than a router would be, and the portal has worked this way since M3-6.
  if (credentialLink) {
    // Keyed for the reason the confirmation panel is: the refusal and the typed password
    // both belong to the credential that was on screen, not to the next one.
    return <SetPassword key={credentialLink} token={credentialLink} onDone={leaveLink} />;
  }

  if (!signedIn) return <Doorway onSignedIn={setSignedIn} />;

  const leave = () => {
    signOut();
    setSignedIn(null);
    // The screen the caller was on is theirs, not the next caller's. Replaced rather than
    // pushed: Back must not walk into a shell whose session has just been given up.
    navigate("/", true);
  };

  if (signedIn.role === "admin") return <Administrator email={signedIn.email} onSignOut={leave} />;

  if (signedIn.role === "lead_auditor") {
    return <LeadAuditor email={signedIn.email} onSignOut={leave} />;
  }

  // Only `admin` and `lead_auditor` are branched above; this is `cb`, whose token always
  // carries a tenant — never null, unlike `lead_auditor`'s.
  return (
    <CertificationBody
      email={signedIn.email}
      tenantId={signedIn.tenantId as string}
      onSignOut={leave}
    />
  );
}

/**
 * The workspace step a path segment names, or null where it names none.
 *
 * Validated against `AUDIT_STEPS` rather than cast, because the segment is whatever was
 * typed into the address bar. An unknown one is not an error to show — the workspace lands
 * on the step the audit has actually reached, which is what a reader with no step at all
 * gets, and the address bar is then corrected to say so.
 */
function stepAt(segment: string | undefined): StepKey | null {
  return AUDIT_STEPS.some((step) => step.key === segment) ? (segment as StepKey) : null;
}

/**
 * The lead auditor's screens — M1-5's competence record, and M3's audits.
 *
 * Which one they land on is not a preference: an auditor who has not submitted their
 * competence record has nothing to be allocated to, and one who has may still have no audit
 * yet. Both live in the sidebar and the audits list answers honestly when it is empty,
 * because "no audit has been allocated to you" is a true and useful thing to be told.
 *
 * An audit is one destination, not six. `AuditWorkspace` owns the steps and the rail across
 * them, so this switch carries the two things the sidebar reaches — the audits list and the
 * competence record — and hands anything inside an audit over whole.
 */
function LeadAuditor({
  email,
  onSignOut,
}: {
  email: string;
  onSignOut: () => void;
}) {
  const path = usePath();
  const view = auditorView(path);
  const step = stepAt(segments(path)[2]);

  // The address bar is made to agree with the screen, not trusted to already. `/` right
  // after signing in, and anything nobody recognises, become the screen actually on show.
  useEffect(() => {
    navigate(auditorPath(auditorView(path), stepAt(segments(path)[2])), true);
  }, [path]);

  const item = (screen: AuditorView["screen"], label: string) => (
    <button
      type="button"
      className={view.screen === screen ? "navitem on" : "navitem"}
      onClick={() => navigate(auditorPath({ screen } as AuditorView, null))}
    >
      {label}
    </button>
  );

  return (
    <AppShell
      who={email}
      role="Lead auditor"
      navLabel="Lead auditor"
      nav={
        <>
          {item("audits", "My audits")}
          {item("record", "Competence record")}
        </>
      }
      onSignOut={onSignOut}
    >
      {view.screen === "audits" ? (
        <MyAudits onOpen={(auditId) => navigate(auditorPath({ screen: "audit", auditId }, null))} />
      ) : view.screen === "record" ? (
        <MyCompetenceRecord />
      ) : (
        <AuditWorkspace
          auditId={view.auditId}
          role="lead_auditor"
          backLabel="My audits"
          step={step}
          onStep={(next, replace) => navigate(auditorPath(view, next), replace)}
          onBack={() => navigate(auditorPath({ screen: "audits" }, null))}
        />
      )}
    </AppShell>
  );
}

type AuditorView =
  | { screen: "audits" }
  | { screen: "record" }
  | { screen: "audit"; auditId: string };

/**
 * The auditor's view as a path, and back again.
 *
 * The pair is written next to the union it encodes so that adding a screen and forgetting
 * its address is one edit rather than two files. Reading is total — every path resolves to
 * a screen, and one that resolves to the wrong one is corrected in the address bar rather
 * than refused, because a mistyped URL is not a thing to show an error page for.
 */
function auditorPath(view: AuditorView, step: StepKey | null): string {
  if (view.screen === "record") return "/record";
  if (view.screen === "audit") {
    return step ? `/audits/${view.auditId}/${step}` : `/audits/${view.auditId}`;
  }
  return "/audits";
}

function auditorView(path: string): AuditorView {
  const [first, second] = segments(path);
  if (first === "record") return { screen: "record" };
  if (first === "audits" && second) return { screen: "audit", auditId: second };
  return { screen: "audits" };
}

/**
 * The certification body's screens, and what it may reach at each stage.
 *
 * **Before submission there is one screen and it is the application.** A body that has not
 * said what it is accredited for cannot be shown an audits list, because every audit is
 * configured inside that accredited scope — an empty list would be an invitation to work
 * the platform would then have to refuse. So the wizard is the whole shell until
 * `onboarding_completed_at` is set.
 *
 * **After submission the sidebar is screen 22's and 23's**: audits, the auditor register,
 * the organisation profile. The application stops being a form and becomes the profile,
 * which is what screen 22 is — the same record, read back rather than typed.
 *
 * The two are one condition and the reversal is deliberate. M1-4's "request more
 * information" clears `onboarding_completed_at`, so a body handed its application back
 * finds the wizard again, holding what it already sent. Nothing here special-cases that;
 * it falls out of reading the one column that means *submitted*.
 */
function CertificationBody({
  email,
  tenantId,
  onSignOut,
}: {
  email: string;
  tenantId: string;
  onSignOut: () => void;
}) {
  const path = usePath();
  const view = cbView(path);
  const step = stepAt(segments(path)[2]);
  const [submitted, setSubmitted] = useState<boolean | null>(null);
  const [name, setName] = useState<string | null>(null);
  const mark = useBrandMark(tenantId);

  useEffect(() => {
    readApplication()
      .then((record) => {
        setSubmitted(record.onboarding_completed_at !== null);
        setName(record.legal_name);
      })
      .catch(() => setSubmitted(false));
  }, []);

  // As the auditor's, with the wizard as its own address. A body that has not submitted has
  // one screen, so every path it could hold is that screen and the bar should say which —
  // and M1-4 handing an application back moves it here from wherever the reader stood.
  useEffect(() => {
    if (submitted === null) return;
    if (!submitted) {
      navigate("/application", true);
      return;
    }
    navigate(cbPath(cbView(path), stepAt(segments(path)[2])), true);
  }, [path, submitted]);

  if (submitted === null) {
    return (
      <AppShell
        who={email}
        role="Certification body"
        navLabel="Certification body"
        nav={null}
        onSignOut={onSignOut}
      >
        <p className="sub">Reading your certification body&hellip;</p>
      </AppShell>
    );
  }

  if (!submitted) {
    return (
      <AppShell
        who={email}
        role="Certification body"
        navLabel="Certification body"
        nav={<span className="navitem on">Application</span>}
        mark={mark}
        markAlt={name ?? ""}
        onSignOut={onSignOut}
      >
        <CertificationBodyOnboarding tenantId={tenantId} />
      </AppShell>
    );
  }

  const item = (screen: CbView["screen"], label: string) => (
    <button
      type="button"
      className={view.screen === screen ? "navitem on" : "navitem"}
      onClick={() => navigate(cbPath({ screen } as CbView, null))}
    >
      {label}
    </button>
  );

  // Screens 22 and 23 draw four items. "New audit" arrives with M2-2 and is a *route*
  // rather than a filter on the list, because an intake in progress is its own screen with
  // its own draft — leaving it and coming back has to find what was typed.
  const nav = (
    <>
      {item("audits", "Audits")}
      <button
        type="button"
        className={view.screen === "intake" ? "navitem on" : "navitem"}
        onClick={() => navigate(cbPath({ screen: "intake" }, null))}
      >
        New audit
      </button>
      {item("register", "Auditor register")}
      {item("profile", "Organisation profile")}
    </>
  );

  return (
    <AppShell
      who={email}
      role="Certification body"
      navLabel="Certification body"
      nav={nav}
      mark={mark}
      markAlt={name ?? ""}
      onSignOut={onSignOut}
    >
      {view.screen === "audits" ? (
        <AuditsList
          onOpenAudit={(auditId) => navigate(cbPath({ screen: "audit", auditId }, null))}
          onOpenDraft={(draftId) => navigate(cbPath({ screen: "intake", draftId }, null))}
          onNewAudit={() => navigate(cbPath({ screen: "intake" }, null))}
        />
      ) : view.screen === "intake" ? (
        <IntakeForm
          // Remounted per draft, so resuming one never shows the previous form's state.
          key={view.draftId ?? "new"}
          draftId={view.draftId}
          onCancel={() => navigate(cbPath({ screen: "audits" }, null))}
          onCreated={(auditId) => navigate(cbPath({ screen: "audit", auditId }, null))}
        />
      ) : view.screen === "audit" ? (
        // Every step of the audit, and what the body may do at each. It reads the plan, the
        // readiness and the report and amends none of them — those are the lead auditor's
        // professional judgement and carry their name — and it watches its own session
        // without a composer. All of that is `audit/steps.ts`, in one place, rather than a
        // route list here that the server would have to keep disagreeing with.
        <AuditWorkspace
          auditId={view.auditId}
          role="cb"
          backLabel="All audits"
          step={step}
          onStep={(next, replace) => navigate(cbPath(view, next), replace)}
          onBack={() => navigate(cbPath({ screen: "audits" }, null))}
        />
      ) : view.screen === "profile" ? (
        <OrganisationProfile tenantId={tenantId} />
      ) : view.screen === "register" ? (
        <AuditorRegister
          onOpen={(auditorId) => navigate(cbPath({ screen: "auditor", auditorId }, null))}
        />
      ) : (
        <VerifyCompetenceRecord
          auditorId={view.auditorId}
          onBack={() => navigate(cbPath({ screen: "register" }, null))}
          onDone={() => navigate(cbPath({ screen: "register" }, null))}
        />
      )}
    </AppShell>
  );
}

type CbView =
  | { screen: "audits" }
  | { screen: "intake"; draftId?: string }
  | { screen: "audit"; auditId: string }
  | { screen: "profile" }
  | { screen: "register" }
  | { screen: "auditor"; auditorId: string };

/**
 * The body's view as a path, and back again.
 *
 * `register` is `/auditors` rather than `/register`: the sidebar calls it the auditor
 * register and the administrator's screen of the same name is a register of certification
 * bodies. Two screens named "register" would be one address either way — this way the
 * address says which register, and neither role has to know about the other's.
 */
function cbPath(view: CbView, step: StepKey | null): string {
  switch (view.screen) {
    case "intake":
      return view.draftId ? `/intake/${view.draftId}` : "/intake";
    case "audit":
      return step ? `/audits/${view.auditId}/${step}` : `/audits/${view.auditId}`;
    case "profile":
      return "/profile";
    case "register":
      return "/auditors";
    case "auditor":
      return `/auditors/${view.auditorId}`;
    default:
      return "/audits";
  }
}

function cbView(path: string): CbView {
  const [first, second] = segments(path);
  if (first === "intake") return second ? { screen: "intake", draftId: second } : { screen: "intake" };
  if (first === "audits" && second) return { screen: "audit", auditId: second };
  if (first === "profile") return { screen: "profile" };
  if (first === "auditors") return second ? { screen: "auditor", auditorId: second } : { screen: "register" };
  return { screen: "audits" };
}

/**
 * The administrator's four screens — M6, and the prototype's own sidebar.
 *
 * Screens 70, 09, 71 and 73, in that order and with those names, because that is the nav
 * `70-all-audits-all-tenants.html` draws. The audits list is what the shell opens on: it is
 * the platform's oversight screen, and an administrator arriving to look at *one* body is
 * the exception rather than the rule.
 *
 * The register (71) and the verification queue (09) read the same list and are not the
 * same screen, because activation and verification are not the same decision. The register
 * carries the door; the queue carries the accreditation. Keeping them apart in the
 * navigation is the same reason they are two columns in the database.
 *
 * **M5-7's `OpenAuditRecord` is gone, and it was always going to be.** It made an
 * administrator name the body and the audit because there was no legitimate list to click
 * from yet, and it said so in as many words. Screen 70 is that list. The reasoning it stood
 * on — `breakGlass.granularity` is *one artefact, one audit* — governs the export and still
 * does; it never governed the read, which the specification's role table makes explicit.
 */
function Administrator({ email, onSignOut }: { email: string; onSignOut: () => void }) {
  const path = usePath();
  const view = adminView(path);

  useEffect(() => {
    navigate(adminPath(adminView(path)), true);
  }, [path]);

  const nav = (
    <>
      {/* M6-1, screen 70. Every row that opens writes an access event to that audit's own
          trail, and listing writes one to each body's — the read is not free. */}
      <button
        type="button"
        className={view.screen === "audits" || view.screen === "record" ? "navitem on" : "navitem"}
        onClick={() => navigate(adminPath({ screen: "audits" }))}
      >
        All audits
      </button>
      <button
        type="button"
        className={view.screen === "queue" ? "navitem on" : "navitem"}
        onClick={() => navigate(adminPath({ screen: "queue" }))}
      >
        Verification queue
      </button>
      <button
        type="button"
        className={view.screen === "register" ? "navitem on" : "navitem"}
        onClick={() => navigate(adminPath({ screen: "register" }))}
      >
        Certification bodies
      </button>
      {/* M6-3, screen 73. */}
      <button
        type="button"
        className={
          view.screen === "config" || view.screen === "costing"
            ? "navitem on"
            : "navitem"
        }
        onClick={() => navigate(adminPath({ screen: "config" }))}
      >
        Platform configuration
      </button>
    </>
  );

  return (
    <AppShell
      who={email}
      role="Complao administrator"
      navLabel="Complao admin"
      nav={nav}
      onSignOut={onSignOut}
    >
      {view.screen === "audits" ? (
        <AllAudits onOpen={(row) => navigate(adminPath({ screen: "record", ...row }))} />
      ) : view.screen === "record" ? (
        <AuditRecord
          auditId={view.auditId}
          tenantId={view.tenantId}
          tenantName={view.tenantName}
          onBack={() => navigate(adminPath({ screen: "audits" }))}
        />
      ) : view.screen === "config" ? (
        <PlatformConfiguration
          onOpenCosting={() => navigate(adminPath({ screen: "costing" }))}
        />
      ) : view.screen === "costing" ? (
        <Costing onBack={() => navigate(adminPath({ screen: "config" }))} />
      ) : view.screen === "register" ? (
        <TenantRegister />
      ) : view.screen === "queue" ? (
        <VerificationQueue
          onOpen={(tenantId) => navigate(adminPath({ screen: "verify", tenantId }))}
        />
      ) : (
        <VerifyCertificationBody
          tenantId={view.tenantId}
          onBack={() => navigate(adminPath({ screen: "queue" }))}
          onDone={() => navigate(adminPath({ screen: "queue" }))}
        />
      )}
    </AppShell>
  );
}

type AdminView =
  | { screen: "audits" }
  | { screen: "register" }
  | { screen: "queue" }
  | { screen: "config" }
  | { screen: "costing" }
  | { screen: "record"; auditId: string; tenantId: string; tenantName?: string }
  | { screen: "verify"; tenantId: string };

/**
 * The administrator's view as a path, and back again.
 *
 * One audit record is `/tenants/<tenant>/audits/<audit>` and not `/audits/<audit>`, because
 * reaching into a certification body's file is an explicit act rather than a consequence of
 * holding an administrator token — the read says whose file it is opening, and the address
 * says the same thing. It is the reasoning `readSession`'s `tenant` parameter already
 * carries, written into the URL.
 *
 * `tenantName` is the list row's and does not survive the address bar. It is only the label
 * shown until the record itself arrives with the body's name on it, so a record reached by
 * a refresh or a pasted link is a beat later in naming the body, and never wrong about it.
 */
function adminPath(view: AdminView): string {
  switch (view.screen) {
    case "record":
      return `/tenants/${view.tenantId}/audits/${view.auditId}`;
    case "register":
      return "/tenants";
    case "queue":
      return "/verification";
    case "verify":
      return `/verification/${view.tenantId}`;
    case "config":
      return "/configuration";
    // Nested under configuration rather than given a root of its own, because that is where
    // it is opened from and the address should say so. An administrator who bookmarks it or
    // pastes it to a colleague lands on the costing screen with the trail back to screen 73
    // already in the path.
    case "costing":
      return "/configuration/costing";
    default:
      return "/audits";
  }
}

function adminView(path: string): AdminView {
  const [first, second, third, fourth] = segments(path);
  if (first === "tenants") {
    if (second && third === "audits" && fourth) {
      return { screen: "record", tenantId: second, auditId: fourth };
    }
    return { screen: "register" };
  }
  if (first === "verification") return second ? { screen: "verify", tenantId: second } : { screen: "queue" };
  if (first === "configuration") {
    return second === "costing" ? { screen: "costing" } : { screen: "config" };
  }
  return { screen: "audits" };
}

type Panel = "sign-in" | "register" | "forgot";

/**
 * The one refusal that is a screen rather than a message.
 *
 * It carries its own RFC 9457 `title` for exactly this reason — every other 403 from the
 * door says "Sign-in refused" and is something the reader can only read, while this one has
 * an action behind it. Matching on the title rather than on the sentence means the wording
 * a person sees can be changed without breaking the routing. `MUST_CHOOSE_TITLE` in
 * `routers/auth.py` is the other half of this pair.
 */
const MUST_CHOOSE = "Set a password";

/** The unauthenticated screen: one panel at a time, no route to switch between. */
function Doorway({ onSignedIn }: { onSignedIn: (who: SignedIn) => void }) {
  const [panel, setPanel] = useState<Panel>("sign-in");
  const [mustChoose, setMustChoose] = useState<{
    email: string;
    password: string;
    reason: string;
  } | null>(null);

  if (mustChoose) {
    return (
      <ForcedPasswordChange
        email={mustChoose.email}
        currentPassword={mustChoose.password}
        reason={mustChoose.reason}
        onDone={() => setMustChoose(null)}
      />
    );
  }

  return (
    <main className="signin">
      <div className="signin__panel">
        <div className="signin__brand">
          <div className="mark" aria-hidden="true">
            c
          </div>
          <div>
            <div className="signin__wm">complao</div>
            <div className="tag">AUDIT PLATFORM</div>
          </div>
        </div>
        <p className="signin__tagline">Stage 1 initial certification · remote</p>

        {panel === "sign-in" ? (
          <SignIn
            onSignedIn={onSignedIn}
            onRegister={() => setPanel("register")}
            onForgot={() => setPanel("forgot")}
            onMustChoose={setMustChoose}
          />
        ) : panel === "register" ? (
          <Register onSignIn={() => setPanel("sign-in")} />
        ) : (
          <Forgot onSignIn={() => setPanel("sign-in")} />
        )}
      </div>
    </main>
  );
}

function SignIn({
  onSignedIn,
  onRegister,
  onForgot,
  onMustChoose,
}: {
  onSignedIn: (who: SignedIn) => void;
  onRegister: () => void;
  onForgot: () => void;
  onMustChoose: (who: { email: string; password: string; reason: string }) => void;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const issued = await authenticate(email, password);
      onSignedIn({
        role: issued.role,
        email,
        tenantId: issued.tenant_id,
        userId: issued.user_id,
      });
    } catch (cause) {
      // One refusal is not an error to display but a screen to open — M9-0 §5. The account
      // still carries the password Complao issued it, the password just typed is right, and
      // what it needs is replacing. The server decided that; this only routes it.
      if (cause instanceof ApiError && cause.problem.title === MUST_CHOOSE) {
        onMustChoose({ email, password, reason: cause.message });
        return;
      }
      // Everything else is the server's sentence, not a rewritten one. "Not yet activated"
      // and "the email address or password is incorrect" are different answers and the
      // difference is the whole point of showing what came back.
      setError(cause instanceof ApiError ? cause.message : "Sign-in failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate>
      {error ? (
        <p className="problem" role="alert">
          {error}
        </p>
      ) : null}

      <TextField
        id="email"
        label="Email"
        kind="email"
        value={email}
        onChange={setEmail}
        autoComplete="username"
        required
      />
      <TextField
        id="password"
        label="Password"
        value={password}
        onChange={setPassword}
        autoComplete="current-password"
        secret
        required
      />
      <div className="signin__actions">
        <button className="btn" type="submit" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </div>
      <p className="signin__switch">
        <button type="button" className="linkish" onClick={onForgot}>
          Forgot your password?
        </button>
      </p>
      <p className="signin__switch">
        New certification body?{" "}
        <button type="button" className="linkish" onClick={onRegister}>
          Register
        </button>
      </p>
    </form>
  );
}

/**
 * Ask for a reset link — M9-0.
 *
 * One sentence comes back whether or not the address exists, and the browser must not
 * improve on that. Reporting "no such account" here would hand back the account list that
 * the door's deliberate vagueness protects, and it is the same reasoning registration
 * already follows.
 */
function Forgot({ onSignIn }: { onSignIn: () => void }) {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      setDone(await requestPasswordReset(email));
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "The request could not be sent.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <>
        <p className="callout green" role="status">
          {done}
        </p>
        <p className="signin__switch">
          <button type="button" className="linkish" onClick={onSignIn}>
            Back to sign in
          </button>
        </p>
      </>
    );
  }

  return (
    <form onSubmit={submit} noValidate>
      <p className="signin__tagline">
        Enter the address you sign in with and we will send a link to set a new password.
      </p>
      {error ? (
        <p className="problem" role="alert">
          {error}
        </p>
      ) : null}
      <TextField
        id="reset-email"
        label="Email"
        kind="email"
        value={email}
        onChange={setEmail}
        autoComplete="username"
        required
      />
      <div className="signin__actions">
        <button className="btn" type="submit" disabled={busy}>
          {busy ? "Sending…" : "Send the link"}
        </button>
      </div>
      <p className="signin__switch">
        <button type="button" className="linkish" onClick={onSignIn}>
          Back to sign in
        </button>
      </p>
    </form>
  );
}

/**
 * An address and nothing else.
 *
 * There is no kind to declare: only a certification body registers here. Administrators
 * are seeded or created by command and auditors are invited by their certification body,
 * so a "what are you signing up as?" question would offer two answers that are never the
 * right one. The legal identity, accreditation and people arrive with the application,
 * which the body fills in once it can sign in.
 */
function Register({ onSignIn }: { onSignIn: () => void }) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      setDone(await signUp(email));
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Registration failed.");
    } finally {
      setBusy(false);
    }
  };

  if (done !== null) {
    return (
      <>
        <p className="notice" role="status">
          {done}
        </p>
        <div className="signin__actions" style={{ marginTop: "14px" }}>
          <button type="button" className="btn sec" onClick={onSignIn}>
            Back to sign in
          </button>
        </div>
      </>
    );
  }

  return (
    <form onSubmit={submit} noValidate>
      <p className="sub" style={{ marginBottom: "14px" }}>
        Register your certification body. A Complao administrator activates new
        registrations before they can sign in.
      </p>

      {error ? (
        <p className="problem" role="alert">
          {error}
        </p>
      ) : null}

      <TextField
        id="register-email"
        label="Email"
        kind="email"
        value={email}
        onChange={setEmail}
        autoComplete="username"
        required
      />
      <div className="signin__actions">
        <button className="btn" type="submit" disabled={busy}>
          {busy ? "Registering…" : "Register"}
        </button>
      </div>
      <p className="signin__switch">
        Already registered?{" "}
        <button type="button" className="linkish" onClick={onSignIn}>
          Sign in
        </button>
      </p>
    </form>
  );
}
